/**
 * Created by fers on 05.02.16.
 */
import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {assert} from 'chai';

import GrantAccessMutation from './GrantAccessMutation.react';
import {FormattedMessage, injectIntl, defineMessages, IntlShape} from 'react-intl';
import {LoadingLine} from '../loadingLine/renderLoadingPanel.react';
import MiniLogo from '../app/MiniLogo.react';
import mutationError from '../channel-defs/mutationError';
import TestChannelAuthMutation from "./TestChannelAuthMutation.react";
import {safeJSONparse} from "../../common/util/safeJSONparse";
import {getQueryParams} from '../../common/util/QueryParams';
import {collectUISettings, getUISettings} from '../app/UISettings.react';
import { User } from '../../common/types/types';
import decodeOAuthState from '../utils/decodeOAuthState';
import { saveChannelGrantToken } from '../utils/channelGrantTokenStorage';

const messages = defineMessages({
    title: {id: 'channelGranted.title', 'defaultMessage': 'Application access granted'}
});


function resolveAddonToken(queryParams = {}) {
    const uiSettings = getUISettings() || {};
    return queryParams['token'] || queryParams['authToken'] || uiSettings.token || null;
}

function isJwtLikeToken(token) {
    const parts = token.split('.');
    return parts.length === 3 && parts.every((part) => !!part);
}

function shouldIncludeTokenInOAuthState(token) {
    if (typeof token !== 'string' || !token.length) {
        return false;
    }

    return token.length <= 256 && !isJwtLikeToken(token);
}

function parseOAuthState(queryParams = {}) {
    const decodedState = decodeOAuthState(queryParams['state'] || '');
    return safeJSONparse(decodedState) || {};
}

export function ChannelGrantedOnMount(relay) {
    return new Promise(function(resolve, reject) {
        /* extract 'state' from URL - it is the only variable allowed by OAuth */
        let args = getQueryParams(document.location.search);
        let state = parseOAuthState(args);
        let token = state.token || args['token'] || args['authToken'];

        relay.commitUpdate(
            new GrantAccessMutation({
                args: JSON.stringify({
                    url: document.location.href,
                    ...state,
                }),
                params: JSON.stringify(args),
                ...state,
                token,
            }), {onSuccess: resolve, onFailure: reject}
        );
    });
}

export function testChannelAuth(channel, relay) {
    return new Promise(function(resolve, reject) {
        let metadata = safeJSONparse(decodeOAuthState(channel.channelDef.metadata));
        if (metadata.hasAuthTest) {
            relay.commitUpdate(
                new TestChannelAuthMutation({
                    'channel': channel
                }), {onSuccess: resolve, onFailure: reject}
            );
        } else {
            //this._authTestDone(channel);
            resolve();
        }
    });
}

interface ChannelGrantedProps {
 intl: IntlShape;
 relay?: object;
 user: User;
}

class ChannelGranted extends React.Component<ChannelGrantedProps> {

    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object,
        user: PropTypes.object.isRequired,
    };

    typeName = 'channels';

    static contextTypes = {
        router: PropTypes.object,
    };

    constructor(props) {
        super(props);

        let queryParams = getQueryParams(window.location.search || '');

        this.state = {
            error: !!(queryParams && queryParams.error) || false
        };
    }

    componentDidMount() {
        if (!this.state.error) {
            ChannelGrantedOnMount(this.props.relay).then((response) => {
                if (response.grantAccess && response.grantAccess.result && response.grantAccess.result !== 'ok') {
                    this.setState({
                        "error": true,
                        "errorText": response.grantAccess.result,
                    });
                    return;
                }
                let channel = response.grantAccess.channel;
                if (response.grantAccess.redirectUrl) {
                    window.location = response.grantAccess.redirectUrl;
                    return;
                }

                return testChannelAuth(channel, this.props.relay).then(() => {
                    this.context.router.push(response.grantAccess.url); // redirect
                    // Re-evaluate UI settings only for the URL restored from OAuth state.
                    setTimeout(() => {
                        collectUISettings();
                    }, 0);
                });
            }, mutationError).catch((e) => {
                throw new Error(e);
            });
        }
    }

    render() {
        if (this.state.error) {
            return (
                <AppPanel title={this.props.intl.formatMessage(messages.title)}>
                    <center>
                        {this.state.errorText ?
                            <div>
                                <h4><FormattedMessage defaultMessage="Something went wrong during grant access: " id="channelGranted.grantAccessError2" /></h4>
                                <small><code>{this.state.errorText}</code></small>
                            </div>

                            :
                            <h4><FormattedMessage defaultMessage="Something went wrong during grant access, please start again." id="channelGranted.grantAccessError" /></h4>
                        }
                    </center>
                </AppPanel>
            );
        }

        return (
            <AppPanel title={this.props.intl.formatMessage(messages.title)}>
                <center>
                    <LoadingLine />
                    <MiniLogo color width="150px"/>
                    <p><strong><FormattedMessage defaultMessage="Redirecting…" id="channelGranted.working" /></strong></p>
                </center>
            </AppPanel>
        );
    }
}


export default Relay.createContainer(injectIntl(ChannelGranted), {
    initialVariables: {
    },
    prepareVariables (variables) {
        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                id
            }
        `,
    },
});

// eslint-disable-next-line max-params
export function callGrant(channel, scenario, type, relay) {
    assert.isDefined(relay);

    return new Promise((resolve, reject) => {
        let queryParams = getQueryParams(document.location.search);
        let addonToken = resolveAddonToken(queryParams);

        let onSuccess = (response) => {
            window.location = response.grantAccess.url;

            // never release promise. Otherwise it will finish to user before the redirect
            //deferred.resolve();
        };
        let onFailure = (transaction) => {
            mutationError(transaction);

            reject();
        };

        let args = {
            'type': type,
            'operation': 'grant',
            'channel_id': channel['realId'],
            'organisation_id':  channel['organisation_id'],
        };

        saveChannelGrantToken(addonToken);

        if (shouldIncludeTokenInOAuthState(addonToken)) {
            args['token'] = addonToken;
        }

        if (scenario) {
            args['scenario_id'] = scenario['realId'];
        }

        relay.commitUpdate(
            new GrantAccessMutation(args), {onSuccess: onSuccess, onFailure: onFailure}
        );
    });
}
